import React from "react";
import { Link } from "react-router-dom";
import {
  Truck, RotateCcw, MessageCircle, Mail, ArrowRight, Clock, ShieldCheck, PackageCheck,
} from "lucide-react";
import SEO from "../components/SEO";

const SHIPPING_POINTS = [
  {
    icon: Truck,
    title: "USPS, FedEx, and UPS",
    body: "We ship via USPS, FedEx, and UPS. Carrier selection depends on your location and order size to ensure the fastest and most reliable delivery.",
  },
  {
    icon: PackageCheck,
    title: "Free standard shipping over $300",
    body: "Standard shipping is $14.00, or free on every order over $300. Expedited available for $32.00.",
  },
  {
    icon: Clock,
    title: "24-hour turnaround",
    body: "Orders ship within 24 hours, usually the same day. We pride ourselves on lightning-fast T/A.",
  },
  {
    icon: ShieldCheck,
    title: "Research-use compliant",
    body: "All items are sold legally for laboratory research purposes only.",
  },
];

const RETURNS_POINTS = [
  {
    title: "Damaged on arrival",
    body: "Automatic return and replacement for any product damaged in transit.",
  },
  {
    title: "Full refund: 5 working days",
    body: "Full refund within 5 working days of delivery on unopened, unused products.",
  },
  {
    title: "After 5 working days",
    body: "Exchange only on undamaged, unused products. Reach out and we'll make it right.",
  },
];

const Shipping = () => (
  <main data-testid="shipping-page" className="bg-cream-100 min-h-screen pb-24">
    <SEO
      title="Shipping & Returns"
      description="LuviaRx ships within the United States via USPS, FedEx, and UPS. Standard shipping $14, free over $300. 24-hour turnaround with tracked, discreet packaging."
      path="/shipping"
    />
    {/* Hero */}
    <section className="container-x pt-16 md:pt-24 pb-10">
      <div className="max-w-3xl">
        <span className="overline mb-5 inline-block">Policies · Shipping & Returns</span>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-charcoal-600">
          Shipping &<br />Returns
        </h1>
        <p className="mt-6 text-base text-charcoal-400 leading-relaxed max-w-xl">
          Honest, fast, and human. Here's how we get research compounds onto your bench, and what
          we do when something doesn't go to plan.
        </p>
      </div>
    </section>

    {/* Shipping section */}
    <section className="container-x pt-4">
      <div className="rounded-[28px] bg-gradient-to-br from-[#eef3e8] via-cream-100 to-[#f9efe6] border border-charcoal-100/50 px-6 sm:px-10 py-10 md:py-12">
        <div className="flex items-center gap-3 mb-7">
          <span className="w-9 h-9 rounded-full bg-sage-100 text-sage-700 flex items-center justify-center">
            <Truck className="w-4 h-4" strokeWidth={2} />
          </span>
          <h2 className="font-display font-bold text-2xl md:text-3xl text-charcoal-600 leading-tight">
            Shipping Information
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
          {SHIPPING_POINTS.map(({ icon: Icon, title, body }) => (
            <article
              key={title}
              data-testid={`shipping-point-${title.replace(/\s+/g, "-").toLowerCase()}`}
              className="bg-white/70 backdrop-blur-sm border border-charcoal-100/50 rounded-2xl p-6"
            >
              <div className="flex items-start gap-3">
                <span className="mt-0.5 w-8 h-8 rounded-full bg-cream-200 text-charcoal-600 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4" strokeWidth={2} />
                </span>
                <div>
                  <h3 className="font-display font-bold text-base text-charcoal-600 leading-tight mb-1.5">
                    {title}
                  </h3>
                  <p className="text-sm text-charcoal-400 leading-relaxed">{body}</p>
                </div>
              </div>
            </article>
          ))}
        </div>

        <p className="mt-8 text-sm text-charcoal-400 leading-relaxed max-w-3xl">
          We value each and every customer and look forward to future business with them. We will do
          anything within reason to keep our researchers happy.
        </p>
      </div>
    </section>

    {/* Returns section */}
    <section className="container-x pt-8 md:pt-10">
      <div className="rounded-[28px] bg-gradient-to-br from-[#fde6ea] via-cream-100 to-cream-200 border border-charcoal-100/50 px-6 sm:px-10 py-10 md:py-12">
        <div className="flex items-center gap-3 mb-7">
          <span className="w-9 h-9 rounded-full bg-[#f3d5db] text-[#8a3a4d] flex items-center justify-center">
            <RotateCcw className="w-4 h-4" strokeWidth={2} />
          </span>
          <h2 className="font-display font-bold text-2xl md:text-3xl text-charcoal-600 leading-tight">
            Returns & Refunds Policy
          </h2>
        </div>

        <ol className="space-y-5 max-w-3xl">
          {RETURNS_POINTS.map((p, i) => (
            <li
              key={p.title}
              data-testid={`returns-point-${i + 1}`}
              className="flex items-start gap-4"
            >
              <span className="mt-0.5 w-7 h-7 rounded-full bg-white border border-charcoal-100 text-charcoal-500 font-display font-bold text-xs flex items-center justify-center shrink-0">
                {i + 1}
              </span>
              <div>
                <h3 className="font-display font-bold text-base text-charcoal-600 leading-tight mb-1">
                  {p.title}
                </h3>
                <p className="text-sm text-charcoal-400 leading-relaxed">{p.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>

    {/* Customer support */}
    <section className="container-x pt-8 md:pt-10">
      <div className="rounded-[28px] bg-charcoal-600 text-cream-100 px-6 sm:px-10 py-10 md:py-12 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
        <span className="w-12 h-12 rounded-full bg-cream-100/10 text-cream-100 flex items-center justify-center shrink-0">
          <MessageCircle className="w-5 h-5" strokeWidth={2} />
        </span>
        <div className="flex-1">
          <span className="overline overline-light mb-2 inline-block">Customer Support</span>
          <h2 className="font-display font-bold text-2xl md:text-3xl text-cream-100 leading-tight">
            Something off with your order?
          </h2>
          <p className="mt-3 text-sm md:text-base text-cream-100/75 leading-relaxed max-w-2xl">
            We ship at high volume and we're human. Mistakes can happen, though not very often.
            If anything's wrong, reach out and we'll make it right.
          </p>
          <p
            data-testid="shipping-text-us"
            className="mt-4 text-sm md:text-base text-cream-100/75 leading-relaxed"
          >
            Questions about your order? We&apos;re one text away.
          </p>
          <a
            href="sms:+15612880915"
            data-testid="shipping-sms"
            className="mt-2 inline-flex items-center gap-2 text-cream-100 hover:text-white font-medium text-sm md:text-base"
          >
            💬 Text us: (561) 288-0915
          </a>
        </div>
        <Link
          to="/faq"
          data-testid="shipping-contact-support"
          className="inline-flex items-center gap-2 bg-cream-100 text-charcoal-700 hover:bg-white px-5 py-3 rounded-full text-sm font-medium transition-colors group"
        >
          <Mail className="w-4 h-4" strokeWidth={2} />
          Contact Us
          <ArrowRight
            className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"
            strokeWidth={2}
          />
        </Link>
      </div>
    </section>
  </main>
);

export default Shipping;
